import _ from 'lodash'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import { Consultation } from '@/core/models/consultation.model'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser, selectOnlineUsers } from '@/redux/reducers/auth.reducer'
import {
  selectConsultationById,
  selectConsultationUpdateRequestTracker,
  selectParticipantsByConsultationId,
  updateConsultation,
} from '@/redux/reducers/consultation.reducer'

import DoctorSelectionDialog, { DoctorSelectionDialogProps } from '../components/DoctorSelectionDialog'

type DoctorSelectionDialogContainerProps = Pick<DoctorSelectionDialogProps, 'title'> & {
  consultationId?: string
}

export function EmergencyDoctorSelectionDialogContainer(props: DoctorSelectionDialogContainerProps) {
  const { consultationId, ...rest } = props
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const consultationRef = useRef<Consultation>()

  const { dispatch: consultationUpdateRequestHandlerDispatch } = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.consultation.update.success'), { variant: 'success' })
    },
    onError: () => {
      enqueueSnackbar(t('notification.consultation.update.error'), { variant: 'error' })
    },
    requestTrackerSelector: selectConsultationUpdateRequestTracker,
  })

  const authUser = useAppSelector(selectAuthUser)
  const consultation = useAppSelector((state) => (consultationId ? selectConsultationById(state, consultationId) : undefined))
  const onlineUsers = useAppSelector(selectOnlineUsers)
  const participants = useAppSelector((state) => (consultationId ? selectParticipantsByConsultationId(state, consultationId) : []))

  const doctors = useMemo(
    () =>
      _.map(
        _.filter(participants, (participant) => participant._id !== authUser?._id && onlineUsers.includes(participant._id)),
        (participant) => ({
          id: participant._id,
          name: `${participant.userInfo.name} ${participant.userInfo.surname}`,
        })
      ),
    [authUser, onlineUsers, participants]
  )

  const handleSelectDoctor = (doctorId: string) => {
    if (!consultationId) return
    consultationUpdateRequestHandlerDispatch(updateConsultation(consultationId, { medecin: doctorId, startDate: new Date().toISOString() }))
  }

  useEffect(() => {
    consultationRef.current = consultation
  }, [consultation])

  // choose the first doctor who joins the emergency room and update new start date
  useEffect(() => {
    const consultation = consultationRef.current
    if (!consultation || !doctors.length) return

    if (!consultation.medecin) {
      consultationUpdateRequestHandlerDispatch(updateConsultation(consultation._id, { medecin: doctors[0].id, startDate: new Date().toISOString() }))
    }
  }, [consultationUpdateRequestHandlerDispatch, doctors])

  return <DoctorSelectionDialog {...rest} doctors={doctors} onSelect={handleSelectDoctor} selectedDoctor={consultation?.medecin?._id} />
}

export default EmergencyDoctorSelectionDialogContainer
